import { ChangeEvent, FC, useCallback, useState } from 'react';

import Button from 'components/_core/Button';
import { useModal } from 'components/ModalProvider/hooks';
import { StepProps } from 'components/Stepper/types';
import ClientContactForm from 'features/Work/components/ClientContactForm';
import { useCreateClientContact } from 'features/Work/pages/Clients/hooks/useCreateClientContact';
import { useUpdateClientContact } from 'features/Work/pages/Clients/hooks/useUpdateClientContact';

import { useClientModalContext } from '../../../Modals';
import { ClientModalType } from '../../../Modals/types';
import { useClientFormModalContext } from '../../hooks/useClientFormModalContext';
import { useOrganizationFormFlowContext } from '../hooks/useOrganizationFormFlowContext';
import locales from './locales';

const CreateClientContactForm: FC<StepProps> = ({ goBack }) => {
  const { showModal } = useModal();
  const { client, setClient } = useClientFormModalContext();
  const { selectedContact } = useOrganizationFormFlowContext();
  const { setContact } = useClientModalContext();

  const [clientContact, setClientContact] = useState(selectedContact);

  const { clientId } = client;
  const isNewClientContact = !clientContact?.clientId;

  // Both mutations should only be called in case clientId is not undefined
  const { loading, updateClientContact } = useUpdateClientContact(
    clientId as string,
  );
  const { createClientContact, loading: createContactLoading } =
    useCreateClientContact(clientId as string);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value =
      e.target.name === 'email' ? e.target.value.trim() : e.target.value;
    return setClientContact({ ...clientContact, [e.target.name]: value });
  };

  const onSubmit = useCallback(async () => {
    // If contact exists, update it directly
    // If contact doesn't exist but client does, create contact
    // Else save contact in state before its creation
    if (!isNewClientContact && clientId) {
      await updateClientContact(clientContact);
    } else if (isNewClientContact && clientId) {
      await createClientContact(clientContact);
    } else {
      const isCreatingNewContactForNewClient = !client.contacts.some(
        (contact) => contact.clientContactId === clientContact.clientContactId,
      );
      if (isCreatingNewContactForNewClient) {
        setClient({ ...client, contacts: [...client.contacts, clientContact] });
      } else {
        // is editing a contact for a new client
        const contacts = client.contacts.map((contact) => {
          if (contact.clientContactId === selectedContact.clientContactId) {
            return clientContact;
          }
          return contact;
        });
        setClient({ ...client, contacts });
      }
    }

    goBack();
  }, [
    client,
    clientContact,
    clientId,
    createClientContact,
    goBack,
    isNewClientContact,
    selectedContact,
    setClient,
    updateClientContact,
  ]);

  return (
    <ClientContactForm
      clientContact={clientContact}
      loading={loading || createContactLoading}
      modalTitle={isNewClientContact ? locales.addTitle : locales.editContact}
      onChange={onChange}
      onHeaderBack={() => goBack()}
      onSubmit={onSubmit}
      submitButtonText={
        isNewClientContact ? locales.addContact : locales.editContact
      }
    >
      {!isNewClientContact ? (
        <Button
          onClick={() => {
            setContact({
              ...clientContact,
              clientContactId: clientContact.clientContactId ?? '',
              clientId: clientContact.clientId ?? '',
            });
            showModal(ClientModalType.DeleteContact);
          }}
          variant="danger"
        >
          {locales.deleteContact}
        </Button>
      ) : null}
    </ClientContactForm>
  );
};

export default CreateClientContactForm;
